/**
 * Time Analysis Tab
 *
 * Time-based performance analysis with heatmaps and patterns.
 */

import { useMemo } from "react";
import { formatCurrency, cn } from "@/lib/utils";
import { useTradingAnalysis } from "../trading-analysis-context";
import { StatCard } from "./shared/stat-card";
import { StatGrid } from "./shared/stat-grid";
import { AnalysisCard } from "./shared/analysis-card";
import { MiniHeatmap } from "./shared/mini-heatmap";
import { formatDuration } from "../hooks";
import { GlobeIcon } from "lucide-react";
import { useI18n } from "@/features/i18n";
import { getTimezoneOption } from "@nordex/i18n";

/**
 * Get the offset in hours for a given timezone
 */
function getTimezoneOffsetHours(timezone: string): number {
  try {
    // Create a reference date
    const now = new Date();
    // Get the offset by comparing UTC and local time strings
    const utcString = now.toLocaleString("en-US", { timeZone: "UTC", hour: "numeric", hour12: false });
    const localString = now.toLocaleString("en-US", { timeZone: timezone, hour: "numeric", hour12: false });
    
    // Parse hours
    const utcHour = parseInt(utcString, 10);
    const localHour = parseInt(localString, 10);
    
    // Calculate difference (handle day boundary)
    let diff = localHour - utcHour;
    if (diff > 12) diff -= 24;
    if (diff < -12) diff += 24;
    
    return diff;
  } catch {
    return 0;
  }
}

/**
 * Convert UTC hour range to local timezone and format as string
 */
function formatSessionHourRange(startHourUTC: number, endHourUTC: number, timezone: string, tzLabel: string): string {
  const offset = getTimezoneOffsetHours(timezone);
  
  // Convert to local time
  let startLocal = (startHourUTC + offset + 24) % 24;
  let endLocal = (endHourUTC + offset + 24) % 24;
  
  // Format as HH:00
  const startStr = startLocal.toString().padStart(2, "0") + ":00";
  const endStr = endLocal.toString().padStart(2, "0") + ":00";
  
  return `${startStr} - ${endStr} ${tzLabel}`;
}

// Session UTC hours configuration
const SESSION_UTC_HOURS = {
  asia: { start: 0, end: 8 },
  europe: { start: 8, end: 16 },
  us: { start: 14, end: 22 },
} as const;

export function TimeAnalysisTab() {
  const { stats, filteredTrades, timeAnalysis, behavior } = useTradingAnalysis();
  const { timezone } = useI18n();

  // Get timezone abbreviation for display
  const timezoneOption = getTimezoneOption(timezone);
  const tzLabel = timezoneOption?.label?.match(/\(([^)]+)\)/)?.[1] || timezone;
  
  // Convert session hour ranges to user's timezone
  const sessionHourRanges = useMemo(() => ({
    asia: formatSessionHourRange(SESSION_UTC_HOURS.asia.start, SESSION_UTC_HOURS.asia.end, timezone, tzLabel),
    europe: formatSessionHourRange(SESSION_UTC_HOURS.europe.start, SESSION_UTC_HOURS.europe.end, timezone, tzLabel),
    us: formatSessionHourRange(SESSION_UTC_HOURS.us.start, SESSION_UTC_HOURS.us.end, timezone, tzLabel),
  }), [timezone, tzLabel]);

  const isEmpty = !filteredTrades.length;

  // Prepare hourly heatmap data
  const hourlyHeatmapData = timeAnalysis.hourly.map((h) => ({
    id: h.hour,
    label: h.hour.toString().padStart(2, "0"),
    value: h.pnl,
    tooltip: `${h.hour.toString().padStart(2, "0")}:00 ${tzLabel}\nP&L: ${h.pnl >= 0 ? "+" : ""}$${formatCurrency(Math.abs(h.pnl), 0)}\nTrades: ${h.trades}\nWin Rate: ${h.winRate.toFixed(0)}%`,
  }));

  return (
    <div className="space-y-4">
      {/* Row 1: Hourly Performance + Trade Duration Analysis (50/50) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Hourly Performance */}
        <AnalysisCard
          title="Hourly Performance"
          subtitle="Find your most profitable trading hours"
        >
          <div className="space-y-4">
            {/* Best/Worst Hours - Now at TOP */}
            <div className="grid grid-cols-2 gap-3">
              {isEmpty ? (
                <>
                  <div className="bg-muted/20 rounded-lg p-3">
                    <div className="text-[10px] text-muted-foreground">Best Hour</div>
                    <div className="text-lg font-bold text-foreground">—</div>
                    <div className="text-xs text-muted-foreground">$0 • 0 trades</div>
                  </div>
                  <div className="bg-muted/20 rounded-lg p-3">
                    <div className="text-[10px] text-muted-foreground">Worst Hour</div>
                    <div className="text-lg font-bold text-foreground">—</div>
                    <div className="text-xs text-muted-foreground">$0 • 0 trades</div>
                  </div>
                </>
              ) : (
                <>
                  {timeAnalysis.bestHour && (
                    <div className="bg-bid/10 rounded-lg p-3">
                      <div className="text-[10px] text-muted-foreground">Best Hour</div>
                      <div className="text-lg font-bold text-bid-foreground">
                        {timeAnalysis.bestHour.hour.toString().padStart(2, "0")}:00 {tzLabel}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        +${formatCurrency(timeAnalysis.bestHour.pnl, 0)} • {timeAnalysis.bestHour.trades} trades
                      </div>
                    </div>
                  )}
                  {timeAnalysis.worstHour && (
                    <div className="bg-ask/10 rounded-lg p-3">
                      <div className="text-[10px] text-muted-foreground">Worst Hour</div>
                      <div className="text-lg font-bold text-ask-foreground">
                        {timeAnalysis.worstHour.hour.toString().padStart(2, "0")}:00 {tzLabel}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        ${formatCurrency(timeAnalysis.worstHour.pnl, 0)} • {timeAnalysis.worstHour.trades} trades
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Heatmap */}
            <div className="overflow-x-auto relative">
              <MiniHeatmap cells={hourlyHeatmapData} size="lg" showLabels />
              {isEmpty && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-xs text-muted-foreground bg-card/80 px-3 py-1 rounded">Hourly data will appear here</p>
                </div>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground text-center">
              Each cell shows your P&L for that hour ({tzLabel}). Hover for details.
            </div>
          </div>
        </AnalysisCard>

        {/* Trade Duration Analysis */}
        <AnalysisCard title="Trade Duration Analysis" subtitle="How long you hold positions">
          <div className="space-y-4">
            {/* Duration Stats - On top */}
            <StatGrid columns={2} gap="sm">
              <StatCard
                label="Avg Duration"
                value={isEmpty ? "0s" : formatDuration(stats.avgTradeDurationMs)}
                tooltip="Average time between opening and closing a position"
              />
              <StatCard
                label="Trading Days"
                value={isEmpty ? 0 : stats.tradingDays}
                tooltip="Number of unique days with at least one completed trade"
              />
            </StatGrid>

            {/* Duration Distribution - Below */}
            <div className="space-y-2 relative">
              <div className="text-xs font-medium text-muted-foreground">
                Duration Distribution
              </div>
              {behavior.durationImpact.map((bucket) => {
                const maxTrades = Math.max(
                  ...behavior.durationImpact.map((b) => b.trades),
                  1
                );
                const barWidth = isEmpty ? 0 : (bucket.trades / maxTrades) * 100;

                return (
                  <div key={bucket.label} className="flex items-center gap-2" title={isEmpty ? "" : `${bucket.trades} trades, Win Rate: ${bucket.winRate.toFixed(0)}%`}>
                    <div className="w-16 text-[10px] text-muted-foreground">
                      {bucket.label}
                    </div>
                    <div className="flex-1 h-4 bg-muted/30 rounded overflow-hidden">
                      <div
                        className={cn(
                          "h-full transition-all",
                          isEmpty ? "bg-muted-foreground/20" : (bucket.pnl >= 0 ? "bg-bid-foreground/50" : "bg-ask-foreground/50")
                        )}
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                    <div className="w-8 text-[10px] text-muted-foreground text-right">
                      {isEmpty ? 0 : bucket.trades}
                    </div>
                  </div>
                );
              })}
              {isEmpty && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-xs text-muted-foreground bg-card/80 px-3 py-1 rounded">Duration data will appear here</p>
                </div>
              )}
            </div>
          </div>
        </AnalysisCard>
      </div>

      {/* Row 2: Day of Week + Trading Sessions (50/50) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Day of Week */}
        <AnalysisCard title="Day of Week Performance" subtitle="Which days work best for you">
          <div className="space-y-3">
            {/* Best/Worst Days - Now at TOP */}
            <div className="grid grid-cols-2 gap-2 pb-3 border-b border-border/30">
              {isEmpty ? (
                <>
                  <div className="text-center">
                    <div className="text-[10px] text-muted-foreground">Best Day</div>
                    <div className="text-sm font-bold text-foreground">—</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] text-muted-foreground">Worst Day</div>
                    <div className="text-sm font-bold text-foreground">—</div>
                  </div>
                </>
              ) : (
                <>
                  {timeAnalysis.bestDay && (
                    <div className="text-center">
                      <div className="text-[10px] text-muted-foreground">Best Day</div>
                      <div className="text-sm font-bold text-bid-foreground">
                        {timeAnalysis.bestDay.day}
                      </div>
                    </div>
                  )}
                  {timeAnalysis.worstDay && (
                    <div className="text-center">
                      <div className="text-[10px] text-muted-foreground">Worst Day</div>
                      <div className="text-sm font-bold text-ask-foreground">
                        {timeAnalysis.worstDay.day}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Day bars */}
            <div className="space-y-2 relative">
              {timeAnalysis.daily.map((day) => {
                const maxPnl = Math.max(
                  ...timeAnalysis.daily.map((d) => Math.abs(d.pnl)),
                  1
                );
                const barWidth = isEmpty ? 0 : (Math.abs(day.pnl) / maxPnl) * 100;
                const isPositive = day.pnl >= 0;

                return (
                  <div key={day.day} className="flex items-center gap-2">
                    <div className="w-10 text-xs text-muted-foreground">{day.day}</div>
                    <div className="flex-1 h-6 bg-muted/30 rounded overflow-hidden relative">
                      <div
                        className={cn(
                          "h-full transition-all",
                          isEmpty ? "bg-muted-foreground/10" : (isPositive ? "bg-bid-foreground/60" : "bg-ask-foreground/60")
                        )}
                        style={{ width: `${barWidth}%` }}
                      />
                      <div className="absolute inset-0 flex items-center justify-end pr-2">
                        <span className={cn(
                          "text-[10px] font-medium",
                          isEmpty ? "text-foreground" : (isPositive ? "text-bid-foreground" : "text-ask-foreground")
                        )}>
                          {isEmpty ? "$0" : `${isPositive ? "+" : ""}$${formatCurrency(day.pnl, 0)}`}
                        </span>
                      </div>
                    </div>
                    <div className="w-12 text-[10px] text-muted-foreground text-right">
                      {isEmpty ? "0" : day.winRate.toFixed(0)}% WR
                    </div>
                  </div>
                );
              })}
              {isEmpty && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <p className="text-xs text-muted-foreground bg-card/80 px-3 py-1 rounded">Day of week data will appear here</p>
                </div>
              )}
            </div>
          </div>
        </AnalysisCard>

        {/* Trading Sessions */}
        <AnalysisCard title="Trading Sessions" subtitle="Performance during global market hours">
          <div className="space-y-3 relative">
            {timeAnalysis.sessions.map((session) => {
              const maxPnl = Math.max(
                ...timeAnalysis.sessions.map((s) => Math.abs(s.pnl)),
                1
              );
              const barWidth = isEmpty ? 0 : (Math.abs(session.pnl) / maxPnl) * 100;
              const isPositive = session.pnl >= 0;

              return (
                <div key={session.session} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <GlobeIcon className="size-3 text-muted-foreground" />
                      <span className="text-xs font-medium">{session.label}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {sessionHourRanges[session.session as keyof typeof sessionHourRanges]}
                      </span>
                    </div>
                    <span className={cn(
                      "text-sm font-bold",
                      isEmpty ? "text-foreground" : (isPositive ? "text-bid-foreground" : "text-ask-foreground")
                    )}>
                      {isEmpty ? "$0" : `${isPositive ? "+" : ""}$${formatCurrency(session.pnl, 0)}`}
                    </span>
                  </div>
                  <div className="h-2 bg-muted/30 rounded overflow-hidden">
                    <div
                      className={cn(
                        "h-full transition-all",
                        isEmpty ? "bg-muted-foreground/10" : (isPositive ? "bg-bid-foreground/60" : "bg-ask-foreground/60")
                      )}
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[10px] text-muted-foreground">
                    <span>{isEmpty ? 0 : session.trades} trades</span>
                    <span>{isEmpty ? "0" : session.winRate.toFixed(0)}% win rate</span>
                  </div>
                </div>
              );
            })}
            {isEmpty && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-xs text-muted-foreground bg-card/80 px-3 py-1 rounded">Session data will appear here</p>
              </div>
            )}
          </div>
        </AnalysisCard>
      </div>
    </div>
  );
}
